import React, { useEffect, useState } from 'react';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useTranslate } from '@tolgee/react';
import 'react-bootstrap-typeahead/css/Typeahead.css';

export interface WebFont {
  family: string;
  category: string;
  variants: string[];
  subsets: string[];
  version: string;
  lastModified: string;
  files: Record<string, string>;
  selectedVariant?: any | undefined;
}

interface FontSelectorProps {
  selectedFont: WebFont | null; // From parent
  onFontChange: (font: WebFont | null) => void;
  options: WebFont[];
  filter?: (font: WebFont) => boolean;
}

export const FontSelector: React.FC<FontSelectorProps> = ({
  selectedFont,
  onFontChange,
  options,
  filter
}) => {
  const { t } = useTranslate();

  // Local state holding the item(s) that Typeahead considers "selected."
  // Typeahead expects an array even if it's single-select.
  const [selected, setSelected] = useState<WebFont[]>([]);

  // Filter down the options if needed
  const filteredOptions = filter ? options.filter(filter) : options;

  // Function to dynamically load the chosen font (example usage)
  const addFontFace = (font: WebFont) => {
    // e.g., load the "regular" variant
    const face = new FontFace(font.family, `url(${font.files.regular})`);
    face.load().then(() => {
      document.fonts.add(face);
    });
  };

  // Sync local "selected" array with the `selectedFont` from parent
  // whenever `selectedFont` or `filteredOptions` changes.
  useEffect(() => {
    if (selectedFont) {
      // Find an existing option in the array by its unique property (family)
      const match = filteredOptions.find(
        (item) => item.family === selectedFont.family
      );
      if (match) {
        // We found the same object from the array
        setSelected([match]);
        addFontFace(match);
      } else {
        // The "selectedFont" isn’t in our list or is missing — clear selection or handle otherwise
        setSelected([]);
      }
    } else {
      setSelected([]);
    }
  }, [selectedFont, filteredOptions]);

  // When the user changes the Typeahead selection, we:
  // 1) load the font
  // 2) call the parent’s onFontChange
  // 3) update local "selected" state to keep Typeahead controlled
  const handleChange = (selectedItems: any) => {
    const chosen = selectedItems[0];
    if (chosen) {
      onFontChange(chosen);
      setSelected([chosen]);
    } else {
      onFontChange(null);
      setSelected([]);
    }
  };

  return (
    <div>
      <Typeahead
        id="google-font-selector"
        labelKey="family" // We match by 'family' field
        options={filteredOptions}
        placeholder={t(
          'lomavis.settings.branding.template_branding.font_selector_placeholder'
        )}
        onChange={handleChange}
        selected={selected} // CONTROLLED: we use local state
        renderMenuItemChildren={(option) => {
          const font = option as WebFont;
          return (
            <div>
              <strong>{font.family}</strong>
            </div>
          );
        }}
      />

      <div className="mt-3" />
      {selectedFont && (
        <link href={selectedFont?.files?.regular} rel="stylesheet" />
      )}

      <div className="w-100 card shadow-sm p-5 text-center">
        <p
          style={{
            fontFamily: selectedFont?.family,
            fontSize: 15
          }}
        >
          {t('lomavis.settings.branding.template_branding.font_preview')}
        </p>
      </div>
    </div>
  );
};
